import { useEffect, useState } from "react";
import { createClient } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { DataTable, DataTableColumn } from '@/components/ui/data-table';

export default function MatriculasAdminPage() {
  const supabase = createClient()
  const [matriculas, setMatriculas] = useState<any[]>([]);
  const [filtered, setFiltered] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [alunos, setAlunos] = useState<any[]>([]);
  const [cursos, setCursos] = useState<any[]>([]);
  const [form, setForm] = useState({ user_id: '', course_id: '' });
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('enrollments')
        .select('id, created_at, user_id, course_id, users:users(id, name, email), courses:courses(id, title)')
        .order('created_at', { ascending: false });
      setMatriculas(data || []);
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchAlunos = async () => {
      const { data } = await supabase.from('users').select('id, name, email').order('name');
      setAlunos(data || []);
    };
    const fetchCursos = async () => {
      const { data } = await supabase.from('courses').select('id, title').order('title');
      setCursos(data || []);
    };
    fetchAlunos();
    fetchCursos();
  }, []);

  useEffect(() => {
    if (!search) {
      setFiltered(matriculas);
    } else {
      const s = search.toLowerCase();
      setFiltered(
        matriculas.filter((m: any) =>
          (m.users?.name?.toLowerCase().includes(s) ||
            m.users?.email?.toLowerCase().includes(s) ||
            m.courses?.title?.toLowerCase().includes(s))
        )
      );
    }
  }, [search, matriculas]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.user_id || !form.course_id) return;
    setCreating(true);
    const { error } = await supabase.from('enrollments').insert({ user_id: form.user_id, course_id: form.course_id });
    if (!error) {
      toast.success('Matrícula criada!');
      setOpen(false);
      setForm({ user_id: '', course_id: '' });
      // Recarregar lista
      const { data } = await supabase
        .from('enrollments')
        .select('id, created_at, user_id, course_id, users:users(id, name, email), courses:courses(id, title)')
        .order('created_at', { ascending: false });
      setMatriculas(data || []);
    } else {
      toast.error('Erro ao criar matrícula.');
    }
    setCreating(false);
  };

  const matriculaColumns: DataTableColumn<any>[] = [
    { key: 'name', label: 'Aluno', render: m => m.users?.name || '-' },
    { key: 'email', label: 'Email', render: m => m.users?.email || '-' },
    { key: 'course', label: 'Curso', render: m => m.courses?.title || '-' },
    { key: 'created_at', label: 'Data', render: m => m.created_at ? new Date(m.created_at).toLocaleString('pt-BR') : '-' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Matrículas</h2>
          <p className="text-muted-foreground">
            Gerencie todas as matrículas de alunos nos cursos da plataforma
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Input
            placeholder="Buscar por aluno, e-mail ou curso..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-56"
          />
          <Dialog open={open} onOpenChange={(v) => v ? setOpen(true) : setOpen(false)}>
            <DialogTrigger asChild>
              <Button className="w-full md:w-auto" onClick={() => { setForm({ user_id: '', course_id: '' }); }}>
                <Plus className="mr-2 h-4 w-4" />
                Nova Matrícula
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nova Matrícula</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Aluno</label>
                  <select
                    className="w-full border rounded px-2 py-1"
                    value={form.user_id}
                    onChange={e => setForm(f => ({ ...f, user_id: e.target.value }))}
                    required
                  >
                    <option value="">Selecione um aluno</option>
                    {alunos.map((a: any) => (
                      <option key={a.id} value={a.id}>{a.name} ({a.email})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Curso</label>
                  <select
                    className="w-full border rounded px-2 py-1"
                    value={form.course_id}
                    onChange={e => setForm(f => ({ ...f, course_id: e.target.value }))}
                    required
                  >
                    <option value="">Selecione um curso</option>
                    {cursos.map((c: any) => (
                      <option key={c.id} value={c.id}>{c.title}</option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                  <Button type="submit" disabled={creating || !form.user_id || !form.course_id}>Criar</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="rounded-md border">
        <DataTable
          columns={matriculaColumns}
          data={filtered}
          loading={loading}
        />
      </div>
    </div>
  );
} 